const EMBED_BASE_URL = 'https://www.google.com/maps/embed/v1/place';
const SEARCH_BASE_URL = 'https://www.google.com/maps/search/';

const componentText = (component) =>
    component?.longText || component?.long_name || component?.shortText || component?.short_name || '';

const findAddressComponent = (components = [], ...types) => {
    for (const type of types) {
        const component = components.find(({ types: componentTypes = [] }) =>
            componentTypes.includes(type)
        );
        if (component) return componentText(component).trim();
    }
    return '';
};

const coordinateValue = (coordinate) => {
    const value = typeof coordinate === 'function' ? coordinate() : coordinate;
    const number = Number(value);
    return Number.isFinite(number) ? number : '';
};

export const isValidGoogleMapsLocation = ({ lat, lng } = {}) => {
    if (String(lat ?? '').trim() === '' || String(lng ?? '').trim() === '') return false;
    const latitude = Number(lat);
    const longitude = Number(lng);
    return Number.isFinite(latitude) && latitude >= -90 && latitude <= 90 &&
        Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
};

export const mapGooglePlaceToBranch = (place = {}) => {
    const components = place.addressComponents || place.address_components || [];
    const route = findAddressComponent(components, 'route');
    const streetNumber = findAddressComponent(components, 'street_number');
    const formattedAddress = place.formattedAddress || place.formatted_address || '';
    const address = [route, streetNumber].filter(Boolean).join(' ').trim() ||
        formattedAddress.split(',')[0].trim();
    const displayName = typeof place.displayName === 'string'
        ? place.displayName
        : place.displayName?.text || place.name || '';
    const location = place.location || place.geometry?.location || {};

    const fields = {
        name: displayName.trim(),
        address,
        colony: findAddressComponent(
            components, 'neighborhood', 'sublocality_level_1', 'sublocality'
        ),
        postalCode: findAddressComponent(components, 'postal_code'),
        city: findAddressComponent(
            components, 'locality', 'postal_town', 'administrative_area_level_2'
        ),
        state: findAddressComponent(components, 'administrative_area_level_1')
    };

    return {
        fields: Object.fromEntries(Object.entries(fields).filter(([, value]) => value)),
        googleMaps: {
            placeId: String(place.id || place.placeId || place.place_id || '').trim(),
            location: {
                lat: coordinateValue(location.lat),
                lng: coordinateValue(location.lng)
            }
        }
    };
};

export const buildGoogleMapsEmbedUrl = (googleMaps = {}, apiKey = '') => {
    if (!apiKey) return '';

    const params = new URLSearchParams({ key: apiKey });
    if (googleMaps.placeId) {
        params.set('q', `place_id:${googleMaps.placeId}`);
    } else if (isValidGoogleMapsLocation(googleMaps.location)) {
        params.set('q', `${googleMaps.location.lat},${googleMaps.location.lng}`);
    } else {
        return '';
    }
    return `${EMBED_BASE_URL}?${params.toString()}`;
};

export const buildGoogleMapsSearchUrl = (query, googleMaps = {}) => {
    const params = new URLSearchParams({ api: '1' });
    if (googleMaps.placeId) {
        params.set('query', query || `${googleMaps.location?.lat},${googleMaps.location?.lng}`);
        params.set('query_place_id', googleMaps.placeId);
    } else if (isValidGoogleMapsLocation(googleMaps.location)) {
        params.set('query', `${googleMaps.location.lat},${googleMaps.location.lng}`);
    } else {
        return '';
    }
    return `${SEARCH_BASE_URL}?${params.toString()}`;
};

let googleMapsLoader;

const getPlacesLibrary = () => {
    if (window.google?.maps?.places?.PlaceAutocompleteElement)
        return { PlaceAutocompleteElement: window.google.maps.places.PlaceAutocompleteElement };
    if (window.google?.maps?.importLibrary)
        return window.google.maps.importLibrary('places');
    return null;
};

export const loadGooglePlacesLibrary = (apiKey) => {
    if (!apiKey)
        return Promise.reject(new Error('missing-google-maps-api-key'));
    const loadedLibrary = getPlacesLibrary();
    if (loadedLibrary) return Promise.resolve(loadedLibrary);
    if (googleMapsLoader) return googleMapsLoader;

    googleMapsLoader = new Promise((resolve, reject) => {
        const existingScript = document.getElementById('google-maps-javascript-api');
        const loadLibrary = () => {
            const library = getPlacesLibrary();
            if (library?.then) library.then(resolve).catch(reject);
            else if (library) resolve(library);
            else reject(new Error('google-maps-api-not-available'));
        };

        if (existingScript && existingScript.src.includes('callback=__nefroGoogleMapsReady')) {
            existingScript.addEventListener('load', loadLibrary, { once: true });
            existingScript.addEventListener('error', reject, { once: true });
            return;
        }
        if (existingScript) existingScript.remove();

        const script = document.createElement('script');
        script.id = 'google-maps-javascript-api';
        const callbackName = '__nefroGoogleMapsReady';
        window[callbackName] = loadLibrary;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${callbackName}&v=weekly`;
        script.async = true;
        script.onerror = reject;
        document.head.appendChild(script);
    });
    return googleMapsLoader;
};
